import React, { Component, memo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import EditBtnCellRenderer from './editBtnCellRenderer';
import SearchMenu from './searchMenu';
import { getUserRole, getUserDetails, getUserAuthToken } from '../../../../services/common/util';
import { createAudit } from '../../../../services/clinicPortalServices/auditService';
import { auditEnums, serviceConstants } from '../../../../services/common/constants';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { fetchQcSamples, printBarcodes } from '../../../../services/clinicPortalServices/qcSampleService';
import moment from 'moment';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import toastr from 'toastr';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

class QcSampleGrid extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showArchived: false,
            quickFilterText: '',
            columnDefs: [
                {
                    headerName: 'Actions',
                    minWidth: 150,
                    maxWidth: 150,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'editBtnCellRenderer'
                },
                {
                    headerName: 'Status',
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: memo((params) => {
                        if (params.data.isArchived) {
                            return (
                                <i className="fas fa-lock" style={{ color: 'red' }} title="Archived"></i>
                            );
                        }
                        return (
                            <i className="fas fa-lock-open" style={{ color: 'green' }} title="Active"></i>
                        );
                    })
                },
                {
                    headerName: 'Name',
                    minWidth: 150,
                    resizable: true,
                    field: 'name'
                },
                {
                    headerName: 'Analytes',
                    minWidth: 250,
                    resizable: true,
                    valueGetter: (params) => {
                        return (
                            params.data.analyteIds.length +
                            ' (' +
                            params.data.analyteIds.map((item) => item.name).join(', ') +
                            ')'
                        );
                    }
                },
                {
                    headerName: 'Control Type',
                    minWidth: 150,
                    resizable: true,
                    field: 'controlType'
                },
                {
                    headerName: 'Lot Number',
                    minWidth: 150,
                    resizable: true,
                    field: 'lotNumber'
                },
                {
                    headerName: 'Lot Expiration Date',
                    minWidth: 150,
                    resizable: true,
                    field: 'lotExpirationDate',
                    valueGetter: (params) => {
                        if (params.data.lotExpirationDate) {
                            return moment(params.data.lotExpirationDate, 'YYYYMMDD').format('MM/DD/YYYY');
                        }
                    }
                },
                {
                    headerName: 'Creation Date',
                    minWidth: 150,
                    resizable: true,
                    valueGetter: (params) => {
                        return moment
                            .tz(params.data.createdAt, serviceConstants.SYSTEM_TIME_ZONE)
                            .format('MM/DD/YYYY hh:mm:ss A');
                    }
                },
                {
                    headerName: 'Created By',
                    minWidth: 150,
                    resizable: true,
                    field: 'createdBy'
                },
                {
                    headerName: 'Barcode',
                    minWidth: 250,
                    field: 'barcode',
                    cellRenderer: memo((params) => {
                        if (params.data.name) {
                            const url =
                                serviceConstants.API_HOST_NAME +
                                '/files/barcodeImagePDF/' +
                                params.data.name.replace(/[^a-zA-Z0-9]/g, '') +
                                '.pdf?' +
                                new Date().getTime().toString() +
                                '&token=' +
                                getUserAuthToken();
                            return (
                                <div>
                                    {params.data.name.replace(/[^a-zA-Z0-9]/g, '')}
                                    &nbsp;&nbsp;
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            fontSize: '20px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <i className="fa fa-barcode"></i>
                                    </a>
                                </div>
                            );
                        }
                        return <div></div>;
                    })
                }
            ],
            components: {
                editBtnCellRenderer: EditBtnCellRenderer
            },
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true
            },
            rowData: [],
            context: { componentParent: this }
        };
    }

    onGridReady = (params) => {
        this.gridApi = params.api;

        this.loadGridData();
    };

    loadGridData = () => {
        fetchQcSamples(this.state.showArchived).then((res) => {
            this.setState({ rowData: res.data });
        });
    };

    onFilterTextChange = (e) => {
        this.setState({ quickFilterText: e.target.value });
    };

    clearFilter = () => {
        this.gridApi.setFilterModel(null);
        this.setState({ quickFilterText: '' });
        document.getElementById('reset-form').value = '';
    };

    onBtnExport = () => {
        this.gridApi.exportDataAsExcel({
            fileName: 'QC_Sample_Grid_' + moment().format('YYYY_MM_DD_HH_mm_ss') + '.xlsx',
            sheetName: 'QC Samples'
        });
        const userData = JSON.parse(getUserDetails());
        const auditData = {
            identifier: auditEnums.IDENTIFIERS.ExportRecord,
            event_type: auditEnums.EVENTTYPES.QcSampleGridExported,
            user_id: userData._id,
            user_name: userData.user_name + ' (' + userData.role + ')',
            update_string: auditEnums.EVENTTYPES.QcSampleGridExported
        };
        createAudit(auditData);
    };

    onBtnPrintBarcodes = async () => {
        const selectedNodes = this.gridApi.getSelectedNodes();
        const selectedRows = selectedNodes.map((node) => node.data);
        if (selectedRows.length === 0) {
            toastr.warning('No sample selected.');
            return;
        }
        const samples = selectedRows.map((row) => ({
            sample: row.name,
            lotNumber: row.lotNumber,
            lotExpirationDate: row.lotExpirationDate
                ? moment(row.lotExpirationDate, 'YYYYMMDD').format('MM/DD/YYYY')
                : ''
        }));
        const response = await printBarcodes(samples);
        if (response && response.status === 200) {
            toastr.success('Barcodes printed successfully.');
            this.loadGridData();
            window.open(`/common/qcBarcodes/${response.data}`, '_blank');
        } else {
            toastr.error('Failed to print barcodes.');
        }
    };

    handleArchiveToggle = (e) => {
        this.setState({ showArchived: e.target.checked }, () => {
            this.loadGridData();
        });
    };

    render() {
        let columnDefs = /salesperson|audit|salesadmin/.test(getUserRole().toLowerCase())
            ? this.state.columnDefs.filter((x) => x.headerName !== 'Actions')
            : this.state.columnDefs;
        return (
            <div className="clinic-contain">
                <SearchMenu
                    onFilterTextChange={this.onFilterTextChange}
                    clearFilter={this.clearFilter}
                    onBtnExport={this.onBtnExport}
                    onBtnPrintBarcodes={this.onBtnPrintBarcodes}
                    context={this.state.context}
                    showArchived={this.state.showArchived}
                    handleArchiveToggle={this.handleArchiveToggle}
                />
                <div
                    style={{
                        width: '100%',
                        height: 'calc(100vh - 250px)',
                        padding: '15px'
                    }}
                >
                    <ThemeContext.Consumer>
                        {({ themeName }) => (
                            <div
                                id="myGrid"
                                style={{
                                    height: '100%',
                                    width: '100%'
                                }}
                            >
                                <AgGridReact
                                    columnDefs={columnDefs}
                                    defaultColDef={this.state.defaultColDef}
                                    masterDetail={true}
                                    onGridReady={this.onGridReady}
                                    rowData={this.state.rowData}
                                    pagination={true}
                                    paginationAutoPageSize={true}
                                    components={this.state.components}
                                    context={this.state.context}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                    quickFilterText={this.state.quickFilterText}
                                    rowSelection={{ mode: 'multiRow' }}
                                />
                            </div>
                        )}
                    </ThemeContext.Consumer>
                </div>
            </div>
        );
    }
}

export default QcSampleGrid;
